import { Component, effect, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NbDialogService, NbMenuItem, NbSidebarService } from '@nebular/theme';
import { distinctUntilChanged, filter } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AuthService, UserAccess } from '../../services/auth.service';
import { canSeeCreditAnalytics } from '../../pages/credit-analytics/credit-analytics.access';
import { IconService } from '../../services/icon.service';
import {
  OrganisationFeatures,
  OrganisationService,
} from '../../services/organisation.service';
import {
  ConnectedLmsPlatforms,
  LmsRegistryService,
} from '../../services/lms-registry.service';
import { CourseSyncJobTrackerService } from '../../services/course-sync-job-tracker.service';
import { AgentChatService } from '../../agent/agent-chat.service';
import { CreatorFocusService } from '../../services/creator-focus.service';

@Component({
  selector: 'app-dashboard-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
})
export class DashboardLayoutComponent implements OnInit {
  authService: AuthService = inject(AuthService);
  readonly agentChat = inject(AgentChatService);
  /** Fokus-Layout des Creators: Kopf, Menü und Hinweise aus (courses page). */
  readonly creatorFocus = inject(CreatorFocusService);
  /** The agent panel folded the left menu; unfold it again when the panel closes. */
  private sidebarFoldedByAgent = false;

  public menu: NbMenuItem[] = [];

  /** Angebundene LMS-Plattformen. Treibt den LMS-Zweig des Menüs. Startwert
   *  „nichts verbunden", damit das Menü sofort steht und sich nach der
   *  Antwort ergänzt — ein leerer Sidebar-Blitz wäre schlimmer als ein
   *  Eintrag, der 200 ms später dazukommt. */
  private connectedLms: ConnectedLmsPlatforms = {
    moodle: false, ilias: false, lti: false, otherLti: false,
  };

  constructor(
    private iconService: IconService,
    protected organisationService: OrganisationService,
    protected deviceDetectorService: DeviceDetectorService,
    protected nbSidebarService: NbSidebarService,
    protected router: Router,
    protected t: TranslateService,
    private dialogService: NbDialogService,
    private lmsRegistry: LmsRegistryService,
    private courseSyncTracker: CourseSyncJobTrackerService,
  ) {}

  ngOnInit(): void {
    this.createMenu();
    this.refreshConnectedLms();
    this.agentChat.loadStatus();

    // `distinctUntilChanged`, weil beim Abmelden zweimal `undefined` kommt
    // (einmal über `onTokenChange`, einmal direkt aus `logout()`) — ohne das
    // liefe jede Reaktion hier doppelt.
    this.authService.accessToken$
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.createMenu();
        // Nach einem Mandantenwechsel (Impersonieren) gelten andere
        // Verbindungen — ohne das zeigte die Sidebar die des vorigen Kunden.
        this.refreshConnectedLms();
      });

    this.organisationService.organisation$.subscribe(() => {
      this.createMenu();
    });

    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.markActiveMenuItem();
    });

    // Kurs-Syncs, die bei geschlossenem oder neu geladenem Portal weiter-
    // gelaufen sind: weiter abfragen und das Ergebnis-Modal nachholen.
    this.courseSyncTracker.resume();
  }

  /** Lädt die angebundenen LMS und baut das Menü danach neu.
   *
   *  Fehler bleiben still: eine nicht erreichbare Registry darf die Sidebar
   *  nicht leeren. Dann fehlt der LMS-Zweig, alles andere funktioniert. */
  private refreshConnectedLms(): void {
    // Ohne Token gibt es nichts abzufragen: beim Abmelden feuerte das noch
    // `/lms/sites` + `/lms/registrations` ohne Authorization-Header, und der
    // Fehler landete als Toast auf dem Login-Bildschirm.
    if (!this.authService.accessToken$.value) {
      return;
    }

    this.lmsRegistry.getConnectedPlatforms().subscribe({
      next: (platforms) => {
        const changed =
          platforms.moodle !== this.connectedLms.moodle ||
          platforms.ilias !== this.connectedLms.ilias;
        this.connectedLms = platforms;
        if (changed) this.createMenu();
      },
      error: () => {},
    });
  }

  ngAfterViewInit(): void {
    if (this.deviceDetectorService.isMobile()) {
      this.nbSidebarService.compact();

      this.router.events.subscribe(() => {
        this.nbSidebarService.compact();
      });
    }
  }

  sidebarExpanded = true;

  // Opening the agent panel makes room by folding the left menu; closing
  // restores it — but only if the panel was the one that folded it.
  private readonly foldForAgent = effect(() => {
    const open = this.agentChat.open();
    if (open && this.sidebarExpanded) {
      this.toggleSidebar();
      this.sidebarFoldedByAgent = true;
    } else if (!open && this.sidebarFoldedByAgent) {
      this.sidebarFoldedByAgent = false;
      if (!this.sidebarExpanded) this.toggleSidebar();
    }
  });

  toggleSidebar(): void {
    this.nbSidebarService.toggle(true, 'menu-sidebar');
    this.sidebarExpanded = !this.sidebarExpanded;
  }

  async createMenu() {
    this.menu = [];
    const payload = this.authService.accessTokenPayload$.value;
    const hasFeatureSet = (payload?.feature_set_permissions?.length ?? 0) > 0;

    // KI-Agenten is always a group. Its children are the agent overview,
    // the bulk-Placements page (per-client feature `TUTOR_PLACEMENTS`) and
    // the two client-wide agent customisations: Learnings (improve answers)
    // and Regeln & Grenzen (prevent answers). Both are client-level pages
    // whose per-agent counterpart lives in the tutor menu — they belong next
    // to the agents, not in the admin block below. LMS tool registrations
    // live under Integrations (separate tile), never under KI-Agenten.
    const hasTutorPlacements = this.organisationService.hasFeature(
      OrganisationFeatures.TUTOR_PLACEMENTS,
    );

    const agentChildren: NbMenuItem[] = [
      {
        title: this.t.instant('pages.tutors.menu.overview'),
        link: '/tutors',
        // `pathMatch: 'full'` so /tutors/placements doesn't also activate
        // the overview item (Nebular's default is prefix-match, which would
        // highlight both Tutoren and Platzierungen at the same time).
        pathMatch: 'full',
      },
    ];
    if (hasTutorPlacements) {
      agentChildren.push({
        title: this.t.instant('pages.tutors.menu.placements'),
        link: '/tutors/placements',
        // Aux-routes that conceptually belong to the Platzierungen flow but
        // live at sibling URLs — keep this entry highlighted while the user
        // is on them. Honored by `markActiveMenuItem`.
        selectedFor: ['/tutors/lms-registrations'],
      } as NbMenuItem & { selectedFor: string[] });
    }
    // One entry for everything that shapes agent behaviour across the
    // client — Learnings, Skills, MCP connectors, Regeln & Grenzen — as tabs
    // on one page (/agent-setup/<tab>). Same gate the guardrails entry had:
    // feature-set users get a curated menu without client configuration.
    // The skill and connector detail pages belong to it for the highlight.
    if (!hasFeatureSet) {
      agentChildren.push({
        title: this.t.instant('pages.agentSetup.menu'),
        link: '/agent-setup',
        selectedFor: ['/skills', '/mcp-connectors'],
      } as NbMenuItem & { selectedFor: string[] });
    }

    this.menu.push(
      {
        title: this.t.instant('pages.tutors.title'),
        icon: { icon: 'orbi', pack: 'al' },
        expanded: true,
        children: agentChildren,
      },
      {
        title: this.t.instant('pages.knowledgeBase.title'),
        icon: {
          icon: 'knowledge',
          pack: 'al',
        },
        link: '/knowledge-base',
      },
    );

    if (!hasFeatureSet) {
      this.menu.push({
        title: this.t.instant('pages.chatStyling.title'),
        icon: {
          icon: 'chatStyling',
          pack: 'al',
        },
        link: '/chat-styling',
      });
    }

    // Dozent-specific menu items (FAQs, Exercises, Assignments, Participants)
    if (
      this.organisationService.hasFeature(OrganisationFeatures.DOZENT) &&
      this.authService.hasFeatureAccess('DOZENT')
    ) {
      const dozentChildren: NbMenuItem[] = [];

      // Everyone who passes the outer gate gets the full dozent
      // workspace: admins/managers (clientwide), DOZENT feature-set
      // users and normal users without a feature set (feature sets are
      // restrictions, not grants). The backend scopes non-admins to
      // their own dozents/content.
      dozentChildren.push(
        {
          title: this.t.instant('menu.dozent.exercises'),
          link: '/dozent/exercises',
        },
        {
          title: this.t.instant('menu.dozent.participants'),
          link: '/dozent/participants',
        },
        {
          title: this.t.instant('menu.dozent.faqs'),
          link: '/dozent/faqs',
        },
        {
          title: this.t.instant('menu.dozent.messages'),
          link: '/dozent/messages',
        }
      );
      // Dozent-persona overview always visible to dozent users — that's
      // where they create + manage their own dozent-bots.
      dozentChildren.push({
        title: this.t.instant('menu.dozent.dozenten'),
        link: '/dozent/dozenten',
      });

      if (dozentChildren.length > 0) {
        this.menu.push({
          title: this.t.instant('menu.dozent.title'),
          icon: 'book-open-outline',
          expanded: hasFeatureSet,
          children: dozentChildren,
        });
      }
    }

    if (
      this.organisationService.hasFeature(OrganisationFeatures.ANALYTICS) &&
      this.authService.hasFeatureAccess('ANALYTICS')
    ) {
      const analyticsChildren: NbMenuItem[] = [
        // {
        //   title: 'Kurse',
        //   link: '/analytics/courses',
        // },
        // {
        //   title: 'Lernende',
        //   link: '/analytics/learners',
        // },
      ];

      analyticsChildren.push(
        {
          title: this.t.instant('pages.analytics.realtime.title'),
          link: '/analytics/live',
        },
        {
          title: this.t.instant('pages.analytics.events.title'),
          link: '/analytics/events',
        },
        {
          title: this.t.instant('pages.analytics.sessions.title'),
          link: '/analytics/sessions',
        },
        {
          title: this.t.instant('pages.analytics.semantics.title'),
          link: '/analytics/semantics',
        },
        {
          title: this.t.instant('pages.analytics.conversations.title'),
          link: '/analytics/conversations',
        },
      );

      // Credit-Verbrauch des eigenen Mandanten. Sichtbarkeit aus einer Quelle
      // (`credit-analytics.access.ts`); die Route traegt denselben Guard, und
      // die Mandantengrenze zieht das Backend — der Menueeintrag schuetzt nichts.
      if (canSeeCreditAnalytics(this.authService.accessTokenPayload$.value?.role)) {
        analyticsChildren.push({
          title: this.t.instant('pages.analytics.creditAnalytics.title'),
          link: '/analytics/credits',
        });
      }

      // Dozent-specific: per-student analytics
      // if (
      //   hasFeatureSet ||
      //   this.authService.hasAccess(UserAccess.DozentAnalytics)
      // ) {
      //   analyticsChildren.push({
      //     title: this.t.instant('pages.dozent.analytics.participantAnalysis'),
      //     link: '/analytics/dozent-students',
      //   });
      // }

      this.menu.push({
        title: 'Analytics',
        icon: 'activity-outline',
        expanded: false,
        children: analyticsChildren,
      });
    }

    if (
      this.organisationService.hasFeature(
        OrganisationFeatures.CONTENT_GENERATOR,
      ) &&
      this.authService.hasFeatureAccess('CONTENT_GENERATOR')
    ) {
      this.menu.push({
        title: 'Creator',
        icon: {
          icon: 'magicWand',
          pack: 'al',
          options: {},
        },
        link: '/creator',
      });
    }

    // ─── Optional: LMS ───────────────────────────────────────────
    // Was hier steht, richtet sich danach, was der Mandant angebunden hat:
    // nur Moodle → ein Moodle-Eintrag, nur ILIAS → ein ILIAS-Eintrag, beides
    // → eine Gruppe „LMS" mit beiden als Unterpunkten. Ein leerer
    // Sammeleintrag über einem einzigen Kind wäre nur ein Klick mehr.
    //
    // Moodle-Quelle bleibt `clients.features`; das JWT-Flag ist der schnelle
    // Rückfall, kann aber bis zum Token-Refresh veraltet sein, wenn ein
    // Super-Admin die Funktion beim Impersonieren einschaltet.
    const moodleFeature =
      this.organisationService.hasFeature(OrganisationFeatures.MOODLE_DASHBOARD) ||
      !!payload?.moodle_dashboard_enabled;

    const lmsChildren: NbMenuItem[] = [];
    if (moodleFeature && this.connectedLms.moodle) {
      lmsChildren.push({
        title: this.t.instant('menu.moodle.title'),
        icon: { icon: 'moodle', pack: 'al' },
        link: '/moodle',
      });
    }
    if (this.connectedLms.ilias) {
      lmsChildren.push({
        title: this.t.instant('menu.lms.ilias'),
        icon: { icon: 'ilias', pack: 'al' },
        // Die ILIAS-Übersicht, analog zur Moodle-Übersicht: was in ILIAS
        // existiert. Agenten zuzuordnen ist etwas anderes und steht unter
        // Platzierungen.
        link: '/ilias',
      } as NbMenuItem);
    }

    // Moodle-Dashboard ohne angebundenes Moodle trotzdem zeigen: die Funktion
    // ist gebucht, und die Seite erklärt selbst, dass die Verbindung fehlt.
    if (lmsChildren.length === 0 && moodleFeature) {
      lmsChildren.push({
        title: this.t.instant('menu.moodle.title'),
        icon: { icon: 'moodle', pack: 'al' },
        link: '/moodle',
      });
    }

    if (lmsChildren.length === 1) {
      this.menu.push(lmsChildren[0]);
    } else if (lmsChildren.length > 1) {
      this.menu.push({
        title: this.t.instant('menu.lms.title'),
        icon: { icon: 'integration', pack: 'al' },
        expanded: lmsChildren.some((c) => this.router.url.startsWith(c.link || '\u0000')),
        children: lmsChildren,
      });
    }

    // ─── Curriculum (study-program structure + learner awareness) ──
    // Per-client opt-in feature (super_admin toggles CURRICULUM in the org
    // dialog). super_admins always see it regardless of the client feature.
    const curriculumEnabled =
      this.authService.isSuperAdmin() ||
      this.organisationService.hasFeature(OrganisationFeatures.CURRICULUM);
    if (curriculumEnabled) {
      this.menu.push({
        title: this.t.instant('pages.curriculum.title'),
        icon: { icon: 'curriculum', pack: 'al' },
        link: '/curriculum',
      });
    }

    if (!hasFeatureSet) {
      this.menu.push(
        {
          title: this.t.instant('pages.integrations.title'),
          icon: {
            icon: 'integration',
            pack: 'al',
            options: {},
          },
          link: '/integrations',
        },
        {
          title: this.t.instant('pages.usage.title'),
          icon: {
            icon: 'usage',
            pack: 'al',
            options: {},
          },
          link: '/usage',
        },
      );
    }

    // Academy: Lern- und Hilfezentrum — für jede Rolle, einziger Einstieg
    // neben den Links in den Portalhinweisen.
    this.menu.push({
      title: this.t.instant('menu.academy'),
      icon: 'award-outline',
      link: '/academy',
    });

    if (this.authService.hasAccess(UserAccess.SideMenuAdmin)) {
      this.menu.push(
        {
          title: this.t.instant('menu.admin.administration'),
          group: true,
        },
        {
          title: this.t.instant('menu.admin.users'),
          icon: {
            icon: 'user',
            pack: 'al',
          },
          link: '/admin/users',
        },
        {
          title: this.t.instant('menu.admin.learners'),
          icon: {
            icon: 'courses',
            pack: 'al',
          },
          link: '/admin/learners',
        },
        {
          title: this.t.instant('menu.admin.organisations'),
          icon: {
            icon: 'organisation',
            pack: 'al',
          },
          link: '/admin/organisations',
        },
        {
          title: this.t.instant('menu.admin.connections'),
          icon: 'swap-outline',
          link: '/admin/connections',
        },
        {
          title: this.t.instant('menu.admin.audit'),
          icon: 'file-text-outline',
          link: '/admin/audit',
        },
      );
    }

    // Org-wide LangSmith token economy — platform data, super_admin only
    // (the route carries SuperAdminGuard as well).
    if (this.authService.isSuperAdmin()) {
      this.menu.push({
        title: this.t.instant('menu.admin.tokenAnalytics'),
        icon: 'activity-outline',
        link: '/admin/token-analytics',
      });
      this.menu.push({
        title: this.t.instant('menu.admin.referrals'),
        icon: 'people-outline',
        link: '/admin/referrals',
      });
      this.menu.push({
        title: this.t.instant('menu.admin.learningBlockCases'),
        icon: 'bulb-outline',
        link: '/admin/learning-block-cases',
      });
      this.menu.push({
        title: this.t.instant('menu.admin.pricing'),
        icon: 'pricetags-outline',
        link: '/admin/pricing',
      });
    }

    this.markActiveMenuItem();
  }

  private markActiveMenuItem() {
    const currentUrl = this.router.url.split('?')[0];
    // Match a single link against the current URL.
    //   exact = true → only equal paths match (used when pathMatch: 'full');
    //   exact = false → equal OR `/`-bounded prefix (so `/tutors/123` does
    //     not falsely match `/tutorsX`).
    const matches = (link: string, exact: boolean): boolean =>
      exact
        ? currentUrl === link
        : currentUrl === link ||
          currentUrl.startsWith(link + '/') ||
          currentUrl.startsWith(link + '?');

    const mark = (items: NbMenuItem[]) => {
      for (const item of items) {
        if (item.link) {
          // Honor `pathMatch: 'full'` on the menu item — without this the
          // Tutoren overview (`/tutors`) would prefix-match every sub-route
          // (`/tutors/placements`, `/tutors/lms-registrations`, …) and stay
          // highlighted everywhere under /tutors/*.
          const exact = item.pathMatch === 'full';
          // Optional aliases on aux-routes (see Platzierungen entry above).
          const aliases =
            ((item as NbMenuItem & { selectedFor?: string[] }).selectedFor ??
              []) as string[];
          item.selected =
            matches(item.link, exact) ||
            aliases.some((a) => matches(a, false));
        }
        if (item.children?.length) mark(item.children);
      }
    };
    mark(this.menu);
  }
}
